import { css, keyframes, type DefaultTheme } from 'styled-components';

export const safeGap = (gap: string) => css`
  ${
    gap &&
    css`
      column-gap: ${gap};

      & > * + * {
        .flex-unsupported & {
          margin-left: ${gap};
        }
      }
    `
  }
`;

export const safeVerticalGap = (gap: string) => css`
  ${
    gap &&
    css`
      gap: ${gap};
      row-gap: ${gap};

      & > * {
        .flex-unsupported & {
          margin-bottom: ${gap};
        }
      }

      & > *:last-child {
        .flex-unsupported & {
          margin-bottom: 0;
        }
      }
    `
  }
`;

export const safeVerticalHorizontalGap = (gap: string) => css`
  ${
    gap &&
    css`
      gap: ${gap};
      row-gap: ${gap};
      column-gap: ${gap};

      & > * + * {
        .flex-unsupported & {
          margin-bottom: ${gap};
          margin-left: ${gap};
        }
      }
    `
  }
`;

export const userSelectNone = css`
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;

export const loadingBackground = css`
  background-image: linear-gradient(
    to right,
    ${({ theme }) => theme.kallisto.color.common.black['100']} 30%,
    #fbfbfb 50%,
    ${({ theme }) => theme.kallisto.color.common.black['100']} 70%,
    ${({ theme }) => theme.kallisto.color.common.black['100']} 100%
  );
`;

export const loadingBackgroundDark = css`
  background-image: linear-gradient(
    to right,
    ${({ theme }) => theme.kallisto.color.common.black['800']} 30%,
    ${({ theme }) => theme.kallisto.color.common.black['700']} 50%,
    ${({ theme }) => theme.kallisto.color.common.black['800']} 70%,
    ${({ theme }) => theme.kallisto.color.common.black['800']} 100%
  );
`;

const shimmerSweepFrames = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

const shimmerSweep = (highlight: ({ theme }: { theme: DefaultTheme }) => string) => css`
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: linear-gradient(to right, transparent, ${highlight} 50%, transparent);
    animation: ${shimmerSweepFrames} 1.5s linear infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
    }
  }
`;

export const loadAnimation = css`
  background-color: ${({ theme }) => theme.kallisto.color.common.black['100']};
  ${shimmerSweep(({ theme }) => theme.kallisto.color.background.static.backgroundLayer01)};
`;

export const evoLoadAnimation = css`
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundDisabled};
  ${shimmerSweep(({ theme }) => theme.kallisto.color.background.static.backgroundLayer01)};
`;

export const imageLoadingAnimationPrimary = css`
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
  ${shimmerSweep(({ theme }) => theme.kallisto.color.background.static.backgroundLayer01)};
`;

export const NoSelect = css`
  user-select: none;
`;

export const Transitions = css`
  transition-duration: 0.2s, 0.2s;
  transition-timing-function: ease-in, ease-out;
`;

export const borderColor = '#DDDDDF';

export const noSelect = css`
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;

export const noEllipsis = css`
  display: block;
  -webkit-line-clamp: unset;
  -webkit-box-orient: unset;
`;

export const ellipsis = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ellipsisOneLine = css`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ellipsisTwoLines = css`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const hideScrollbar = css`
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
`;

export const focusRing = css`
  outline: 2px solid #0a0c0d;
  outline-offset: 2px;
  box-shadow: 0 0 0 2px white;
  transition: none;
`;

export const evoDropdownCard = css`
  padding: calc(${({ theme }) => theme.kallisto.space.space100} - 1px);
  border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius200};
  box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumDownShadow};
`;

export const evoDropdownOption = css`
  padding: ${({ theme }) => theme.kallisto.space.space100};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius50};

  &:hover {
    background-color: ${({ theme }) => theme.kallisto.color.background.interactive.backgroundNeutralHover};
  }
`;

export const srOnly = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

// Card sizes only flip when the resizing list crosses a container query, so they need less time to catch up.
export const CARD_SIZE_TIMING = '0.15s ease-out';

export const viewSwitchTransition = (properties: string[], timing = '0.3s cubic-bezier(0.4, 0, 0.2, 1)') => css`
  transition: ${properties.map((property) => `${property} ${timing}`).join(', ')};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
